import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { temPapel, useAuth } from "../../lib/auth";
import { mensagemDeErro, novaChave, rpc } from "../../lib/rpc";
import { supabase } from "../../lib/supabase";
import { chamarFuncao } from "../../lib/funcoes";
import { sinalConcluido, sinalErro, sinalOk } from "../../lib/sinais";
import { imprimirPdf } from "../../lib/imprimir";
import { fmtHora, medidas } from "../../lib/formato";
import { type DocumentoImpressao, type EstadoConferencia, nomeDocumento } from "../../lib/tipos";
import { LeituraCodigo } from "../../componentes/LeituraCodigo";
import { AlertaErro, Confirmar } from "../../componentes/Alerta";
import { Botao, Escolha, Selo } from "../../componentes/ui";

import { numeroPedido, podePedidoManual } from "../../lib/pedidosManuais";
import { imprimirPedidoManual } from "../../componentes/PedidoManualImpressao";

const ORDEM_DOC: Record<string, number> = { DANFE_SIMPLIFICADO: 0, ETIQUETA: 1 };
const ordenarDocumentos = (docs: DocumentoImpressao[]) => [...docs].sort((a, b) => (ORDEM_DOC[a.tipo] ?? 9) - (ORDEM_DOC[b.tipo] ?? 9));
const emAndamento = (d: DocumentoImpressao) => d.status === "pendente" || d.status === "processando";

interface Finalizado {
  pedidoId: string;
  numero: string;
  manual: boolean;
  contentor: string | null;
  docs: DocumentoImpressao[];
  buscando: boolean;
  erro: string | null;
  automatico: boolean;
}

interface Leitura { codigo: string; texto: string; ok: boolean; quando: string }

const MOTIVOS = ["Falta de item", "Sobra de item", "Item errado na caixa", "Item avariado", "Outro"];

const textoResultado: Record<string, string> = {
  PRODUTO_ERRADO: "Item não é deste pedido",
  SOBRA: "Unidade a mais",
  CODIGO_DESCONHECIDO: "Código não cadastrado",
  EMBALAGEM_PRIMEIRO: "Bipe primeiro a embalagem, depois os itens",
};

/**
 * Bancada de conferência (notebook touch). Conferência cega: bipa a caixa preta,
 * a embalagem (obrigatória antes dos itens) e os itens; a tela só mostra a %. No 100% com
 * embalagem, finaliza sozinha e busca no Tiny o DANFE simplificado e a etiqueta.
 * Imprimir sozinho ou pelo botão é configuração do admin.
 */
export default function Conferencia() {
  const { perfil } = useAuth();
  const [estado, setEstado] = useState<EstadoConferencia | null>(null);
  const [finalizado, setFinalizado] = useState<Finalizado | null>(null);
  const impressos = useRef(new Set<string>());
  const [leituras, setLeituras] = useState<Leitura[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [lampejo, setLampejo] = useState(0);
  const [tremor, setTremor] = useState(0);
  const [abandonar, setAbandonar] = useState(false);
  const [motivo, setMotivo] = useState(MOTIVOS[0]);
  const [observacao, setObservacao] = useState("");
  const [confirmarZerar, setConfirmarZerar] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  // a impressão de um pedido anterior continua mesmo se a tela já mudou para a próxima caixa
  const pedidoNaTela = useRef<string | null>(null);

  const fila = useQuery({
    queryKey: ["conferencia-fila", perfil?.deposito_padrao_id],
    queryFn: async () => {
      let q = supabase.from("pedidos").select("id", { count: "exact", head: true }).eq("status", "separado");
      if (perfil?.deposito_padrao_id) q = q.eq("deposito_id", perfil.deposito_padrao_id);
      const { count } = await q;
      return count ?? 0;
    },
    refetchInterval: 15_000,
  });

  function registrar(codigo: string, texto: string, ok: boolean) {
    setLeituras((l) => [{ codigo, texto, ok, quando: new Date().toISOString() }, ...l].slice(0, 30));
  }

  function falhou(msg: string, codigo?: string) {
    sinalErro();
    setTremor((n) => n + 1);
    if (codigo) registrar(codigo, msg, false);
    setErro(msg);
  }

  async function iniciar(codigo: string) {
    const r = await rpc<EstadoConferencia>("wms_conferencia_iniciar", { p_codigo_contentor: codigo });
    if (!r.ok) return falhou(r.mensagem ?? "Caixa preta recusada");
    sinalOk();
    setLampejo((n) => n + 1);
    setAviso(null);
    setLeituras([]);
    setEstado(r);
    // caixa reaberta já em 100% (tela recarregada ou fechamento que falhou): fecha agora
    if (podeFinalizar(r)) await finalizar(r);
  }

  const podeFinalizar = (c: EstadoConferencia) => c.completo && (!c.exigir_embalagem || c.embalagens > 0);

  async function finalizar(conf: EstadoConferencia) {
    const r = await rpc<EstadoConferencia>("wms_conferencia_finalizar", { p_conferencia_id: conf.conferencia_id, p_idem: novaChave() });
    if (!r.ok) return falhou(r.mensagem ?? "Não foi possível finalizar");
    sinalConcluido();
    setEstado(null);
    void fila.refetch();
    const fim: Finalizado = {
      pedidoId: r.pedido.id, numero: numeroPedido(r.pedido), manual: r.pedido.origem === "MANUAL", contentor: conf.contentor, docs: [], buscando: r.pedido.origem !== "MANUAL", erro: null,
      automatico: r.imprimir_automatico !== false,
    };
    pedidoNaTela.current = fim.pedidoId;
    setFinalizado(fim);
    if (!fim.manual) void buscarDocumentos(fim, false);
  }

  // Busca o DANFE simplificado e a etiqueta e imprime sozinho (DANFE primeiro), sem mostrar
  // os documentos. A função responde assim que o DANFE fica pronto (imprime na hora) e a
  // etiqueta termina no servidor: consulta de novo a cada 1,5 s nos primeiros 15 s e depois a
  // cada 5 s, por até 2 min, mesmo que o conferente já tenha bipado a próxima caixa.
  // Tudo impresso: o aviso some.
  async function buscarDocumentos(fim: Finalizado, soLinks: boolean) {
    const naTela = () => pedidoNaTela.current === fim.pedidoId;
    const atualizar = (f: Finalizado) => { if (naTela()) setFinalizado(f); };
    atualizar({ ...fim, buscando: true, erro: null });
    try {
      let docs: DocumentoImpressao[] = [];
      for (let tentativa = 0; tentativa < 35; tentativa++) {
        if (tentativa > 0) await new Promise((ok) => setTimeout(ok, tentativa <= 10 ? 1500 : 5000));
        const data = await chamarFuncao<{ ok: boolean; documentos: DocumentoImpressao[] }>("tiny-documentos",
          { pedido_id: fim.pedidoId, acao: soLinks || tentativa > 0 ? "links" : "processar" });
        docs = ordenarDocumentos(data.documentos);
        atualizar({ ...fim, docs, buscando: docs.some(emAndamento), erro: null });
        if (fim.automatico && !soLinks) await imprimirTodos(docs.filter((d) => !impressos.current.has(d.id)));
        if (soLinks || !docs.some(emAndamento)) break;
      }
      atualizar({ ...fim, docs, buscando: false, erro: null });
      const tudoImpresso = docs.length > 0 && docs.every((d) => d.status === "pronto" && (d.impresso_em || impressos.current.has(d.id)));
      if (fim.automatico && !soLinks && tudoImpresso) {
        setTimeout(() => { if (naTela()) { pedidoNaTela.current = null; setFinalizado(null); } }, 4000);
      }
    } catch (e) {
      atualizar({ ...fim, buscando: false, erro: mensagemDeErro(e) });
    }
  }

  async function imprimirTodos(docs: DocumentoImpressao[]) {
    for (const d of ordenarDocumentos(docs)) {
      if (d.status !== "pronto") continue;
      let todos = true;
      for (const link of d.links) todos = (await imprimirPdf(link)) && todos;
      if (!todos) throw new Error("O navegador bloqueou a janela de impressão. Permita pop-ups para localhost e clique em Tentar de novo.");
      impressos.current.add(d.id);
      await rpc("wms_documento_impresso", { p_documento_id: d.id }).catch(() => undefined);
    }
  }

  async function leitura(codigo: string) {
    if (ocupado) return;
    setOcupado(true);
    try {
      if (!estado) {
        await iniciar(codigo);
        return;
      }
      const r = await rpc<EstadoConferencia>("wms_conferencia_bipar", {
        p_conferencia_id: estado.conferencia_id, p_codigo: codigo, p_idem: novaChave(),
      });
      if (r.conferencia_id) setEstado(r);
      if (!r.ok) return falhou(textoResultado[r.codigo_erro ?? ""] ?? r.mensagem ?? "Leitura recusada", codigo);
      sinalOk();
      setLampejo((n) => n + 1);
      registrar(codigo, r.tipo === "embalagem" ? "Embalagem" : "Conferido", true);
      if (podeFinalizar(r)) await finalizar(r);
    } catch (e) {
      falhou(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  }

  async function finalizarAgora() {
    if (!estado || ocupado) return;
    setOcupado(true);
    try {
      await finalizar(estado);
    } catch (e) {
      falhou(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  }

  async function zerar() {
    if (!estado) return;
    setOcupado(true);
    try {
      const r = await rpc<{ ok: boolean; mensagem?: string; contentor?: string }>("wms_conferencia_zerar", { p_pedido_id: estado.pedido.id });
      setConfirmarZerar(false);
      if (!r.ok) return falhou(r.mensagem ?? "Não foi possível zerar");
      setAviso(`Conferência do pedido ${numeroPedido(estado.pedido)} zerada. Bipe a caixa ${r.contentor ?? estado.contentor} para conferir de novo.`);
      setEstado(null);
      setLeituras([]);
      void fila.refetch();
    } catch (e) {
      falhou(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  }

  async function confirmarAbandono() {
    if (!estado) return;
    setOcupado(true);
    try {
      const texto = observacao.trim() ? `${motivo}: ${observacao.trim()}` : motivo;
      const r = await rpc("wms_conferencia_abandonar", { p_conferencia_id: estado.conferencia_id, p_motivo: texto });
      if (!r.ok) return falhou(r.mensagem ?? "Não foi possível abandonar");
      setAviso(`Pedido ${numeroPedido(estado.pedido)} foi para divergência. Entregue a caixa ${estado.contentor} ao líder.`);
      setEstado(null);
      setAbandonar(false);
      setObservacao("");
      void fila.refetch();
    } catch (e) {
      falhou(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  }

  const embalagemPrimeiro = !!estado && estado.exigir_embalagem && estado.embalagens === 0;
  const rotulo = !estado ? "Bipe a caixa preta" : embalagemPrimeiro ? "Bipe primeiro a embalagem (caixa de papelão)" : "Bipe os itens";

  return (
    <div className="grid gap-4 lg:min-h-[calc(100vh-5.5rem)] lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
      <AlertaErro mensagem={erro} onFechar={() => setErro(null)} />

      {/* coluna principal */}
      <div className="flex flex-col gap-4">
        {aviso && <p className="rounded-lg bg-alerta-claro px-4 py-3 text-xl font-semibold text-alerta">{aviso}</p>}
        {finalizado?.manual ? <div role="status" className="flex flex-wrap items-center gap-3 rounded-lg bg-ok-claro px-4 py-3 text-xl font-semibold text-ok">
          <span className="flex-1">Pedido {finalizado.numero} conferido. Pronto para expedição pelo código WMS.</span>
          {podePedidoManual(perfil, "imprimir") && <Botao onClick={() => void imprimirPedidoManual(finalizado.pedidoId).catch(e => setErro(mensagemDeErro(e)))}>Imprimir comprovante</Botao>}
          <Botao variante="fantasma" onClick={() => setFinalizado(null)}>Fechar aviso</Botao>
        </div> : finalizado && <Impressao fim={finalizado}
          onImprimir={() => void imprimirTodos(finalizado.docs).then(() => buscarDocumentos(finalizado, true))}
          onTentar={() => void buscarDocumentos(finalizado, false)} />}

        {estado ? (
          <section className="flex flex-1 flex-col rounded-lg border-2 border-tinta bg-chapa">
            <div className="flex flex-wrap items-center gap-3 border-b-2 border-aco px-5 py-4">
              <span className="font-sinal text-4xl font-bold">Pedido {numeroPedido(estado.pedido)}</span>
              {estado.pedido.tags.map((t) => <Selo key={t} tom="faixa">{t}</Selo>)}
              <span className="ml-auto rounded-md bg-tinta px-3 py-1 font-sinal text-2xl text-white">{estado.contentor}</span>
            </div>
            <div className="flex flex-1 flex-col justify-center gap-4 px-5 py-6">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <span className="font-sinal text-[9rem] font-bold leading-none xl:text-[11rem]" aria-live="polite">{estado.progresso}%</span>
                <span className="text-right text-xl text-aco-escuro">{estado.pedido.canal}<br />{estado.pedido.forma_envio}</span>
              </div>
              <div className="h-8 overflow-hidden rounded-full bg-concreto-escuro" aria-hidden>
                <div className={`h-full transition-all ${estado.completo ? "bg-ok" : "bg-tinta"}`} style={{ width: `${estado.progresso}%` }} />
              </div>
              {estado.embalagem_sugerida && (estado.embalagem_sugerida.caixas.length > 0 || estado.embalagem_sugerida.produtos_sem_caixa > 0) && (
                <div className={`rounded-md border-2 px-4 py-3 ${embalagemPrimeiro ? "border-faixa bg-faixa/30 ring-4 ring-faixa" : "border-tinta bg-concreto"}`} aria-live="polite">
                  <p className="text-sm font-semibold uppercase text-aco-escuro">{embalagemPrimeiro ? "Bipe primeiro a caixa: sugerida" : "Caixa sugerida"}</p>
                  {estado.embalagem_sugerida.caixas.map((c) => (
                    <p key={c.embalagem_id} className="font-sinal text-3xl font-bold">
                      {c.quantidade > 1 ? `${c.quantidade}x ` : ""}{c.codigo ?? c.nome}
                      <span className="ml-3 font-sans text-xl font-normal text-aco-escuro">{c.nome} ({medidas(c)})</span>
                    </p>
                  ))}
                  {estado.embalagem_sugerida.produtos_sem_caixa > 0 && (
                    <p className="text-lg text-alerta">
                      {estado.embalagem_sugerida.caixas.length ? "E mais " : ""}{estado.embalagem_sugerida.produtos_sem_caixa} produto(s) sem caixa definida
                    </p>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-3 text-lg">
                {estado.exigir_embalagem && (
                  <span className={`rounded-md px-4 py-2 font-semibold ${estado.embalagens > 0 ? "bg-ok-claro text-ok" : "bg-alerta-claro text-alerta"}`}>
                    {estado.embalagens > 0 ? `Embalagem bipada (${estado.embalagens})` : "Bipe a embalagem antes dos itens"}
                  </span>
                )}
                {estado.ocorrencias > 0 && (
                  <span className="rounded-md bg-erro-claro px-4 py-2 font-semibold text-erro">{estado.ocorrencias} leitura(s) recusada(s)</span>
                )}
              </div>
            </div>
          </section>
        ) : (
          <section className="flex flex-1 flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-aco bg-chapa p-8 text-center">
            <p className="font-sinal text-5xl font-bold">Bipe a caixa preta</p>
            <p className="text-xl text-aco-escuro">
              {fila.data === undefined ? "" : fila.data === 0 ? "Nenhuma caixa aguardando conferência" : `${fila.data} caixa(s) aguardando conferência`}
            </p>
          </section>
        )}

        <LeituraCodigo rotulo={rotulo} onLeitura={leitura} desabilitado={ocupado} lampejo={lampejo} erro={tremor} />

        {estado && podeFinalizar(estado) && (
          <Botao variante="confirmar" grande disabled={ocupado} onClick={() => void finalizarAgora()}>
            {ocupado ? "Finalizando..." : "Finalizar conferência e imprimir"}
          </Botao>
        )}
        {estado && (
          <div className="flex flex-wrap gap-3">
            <Botao variante="perigo" grande onClick={() => setAbandonar(true)}>Abandonar (falta ou sobra)</Botao>
            <Botao variante="secundario" grande onClick={() => { setEstado(null); setLeituras([]); }}>Trocar de caixa</Botao>
            {temPapel(perfil, "lider") && <Botao variante="fantasma" grande onClick={() => setConfirmarZerar(true)}>Zerar conferência (líder)</Botao>}
          </div>
        )}
      </div>

      {/* coluna lateral */}
      <aside className="flex flex-col gap-4">
        <section className="flex flex-1 flex-col rounded-lg border-2 border-aco bg-chapa">
          <h2 className="border-b-2 border-aco px-4 py-3 font-sinal text-xl font-semibold">Leituras desta caixa</h2>
          {leituras.length ? (
            <ul className="max-h-[50vh] divide-y divide-aco overflow-y-auto">
              {leituras.map((l, i) => (
                <li key={i} className="flex items-center justify-between gap-2 px-4 py-2">
                  <span className="min-w-0">
                    <span className="block truncate font-mono text-sm">{l.codigo}</span>
                    <span className={`font-semibold ${l.ok ? "text-ok" : "text-erro"}`}>{l.texto}</span>
                  </span>
                  <span className="text-sm text-aco-escuro">{fmtHora(l.quando)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="px-4 py-3 text-aco-escuro">Nada bipado ainda.</p>}
        </section>
      </aside>

      <Confirmar aberto={confirmarZerar} titulo="Zerar a conferência deste pedido?" rotuloConfirmar="Zerar" perigo ocupado={ocupado}
        onConfirmar={() => void zerar()} onCancelar={() => setConfirmarZerar(false)}>
        As leituras desta conferência são descartadas (sem divergência para o separador) e o pedido volta a esperar a conferência na
        mesma caixa preta. Nenhum estoque se move.
      </Confirmar>

      <Confirmar aberto={abandonar} titulo="Abandonar a conferência" rotuloConfirmar="Enviar para divergência" perigo ocupado={ocupado}
        onConfirmar={() => void confirmarAbandono()} onCancelar={() => setAbandonar(false)}>
        <div className="flex flex-col gap-3">
          <Escolha rotulo="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)}>
            {MOTIVOS.map((m) => <option key={m}>{m}</option>)}
          </Escolha>
          <label className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-aco-escuro">Observação</span>
            <textarea value={observacao} onChange={(e) => setObservacao(e.target.value)} rows={2}
              className="rounded-md border-2 border-aco p-2 text-lg focus:border-tinta focus:outline-none" />
          </label>
          <p className="text-sm text-aco-escuro">O que não foi conferido sai da caixa no sistema e o pedido vai para o líder resolver.</p>
        </div>
      </Confirmar>
    </div>
  );
}

/** Aviso de uma linha do pedido que acabou de fechar: imprimindo, impresso ou o que falhou. */
function Impressao({ fim, onImprimir, onTentar }: { fim: Finalizado; onImprimir: () => void; onTentar: () => void }) {
  const falhos = fim.docs.filter((d) => d.status !== "pronto" && !emAndamento(d));
  const esperando = !fim.buscando && fim.docs.some(emAndamento);
  const pronto = fim.docs.length > 0 && fim.docs.every((d) => d.status === "pronto");
  const problema = !!fim.erro || falhos.length > 0 || esperando || (!fim.buscando && fim.docs.length === 0);

  let texto = "imprimindo DANFE e etiqueta...";
  if (fim.erro) texto = `não imprimiu: ${fim.erro}`;
  else if (falhos.length) texto = `não imprimiu: ${falhos.map((d) => `${nomeDocumento[d.tipo] ?? d.tipo} (${d.erro ?? d.status})`).join(", ")}`;
  else if (esperando) texto = "o Tiny ainda não liberou o DANFE ou a etiqueta.";
  else if (!fim.buscando && fim.docs.length === 0) texto = "nenhum documento na fila deste pedido.";
  else if (pronto && !fim.automatico && fim.docs.some((d) => !d.impresso_em)) texto = "DANFE e etiqueta prontos.";
  else if (pronto) texto = "DANFE e etiqueta impressos.";

  return (
    <div role="status" aria-live="polite"
      className={`flex flex-wrap items-center gap-3 rounded-lg px-4 py-3 text-xl font-semibold ${problema ? "bg-erro-claro text-erro" : "bg-ok-claro text-ok"}`}>
      <span className="min-w-0 flex-1">Pedido {fim.numero} conferido: {texto}</span>
      {pronto && !fim.automatico && fim.docs.some((d) => !d.impresso_em) && <Botao onClick={onImprimir}>Imprimir</Botao>}
      {!fim.buscando && problema && <Botao variante="secundario" onClick={onTentar}>Tentar de novo</Botao>}
    </div>
  );
}
